import { previewStaticPaths } from 'scripts/preview-routes.cjs'
/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { filterProducts } from 'src/shared/frontendTypes/productsFilter'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'
/* Functions */
import { post } from 'src/functions/fetch'
import { loadTemplateListingRoutes } from 'src/functions/templateListingRoutes.server'
import { loadListingPageData } from 'src/functions/pageData'
import { saveUrl } from 'src/functions/regex'
import { listingPageNumber, listingPagePath } from 'src/functions/listingSeo'
import { mergeListingRoutes } from 'src/functions/templateListingRoutes'

/* Options */
import config from 'src/config'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/index'

const withoutUndefined = <T extends Record<string, unknown>>(values: T): Partial<T> =>
  Object.entries(values).reduce<Partial<T>>((result, [key, value]) => {
    if (value !== undefined) result[key as keyof T] = value as T[keyof T]
    return result
  }, {})

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Erweiterte Props-Schnittstelle um die SEO-Felder und canonicalUrl */
interface Props {
  bid: number
  title?: string
  description?: string
  products: tProductPreview[]
  count: number
  productsGeneratedAt: number
  initialPage: number
  filterMetadata: Pick<filterProducts, 'filterTree' | 'filterBrands'>
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string
  brandLogo?: string
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const FilterPage: React.FC<Props> = ({ 
  bid, title, description, products, count, productsGeneratedAt, initialPage, filterMetadata, metaTitle, metaDescription, metaKeywords, canonicalUrl, brandLogo, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated
}: Props) => 
  <Filter {...{ type: 'brand', bid, title, description, products, count, productsGeneratedAt, initialPage, filterMetadata, metaTitle, metaDescription, metaKeywords, canonicalUrl, brandLogo, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated }} />

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const getStaticPaths: GetStaticPaths = async () => {
  const preview = previewStaticPaths('brand')
  if (preview) return preview

  const size = (() => {
    switch (process.env.CACHE) {
      case 'static': return config.cache_static_brands_size
      case 'ssr':    return config.cache_ssr_brands_size
      default:       return 1
    }
  })()

  if (size <= 0) {
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const data = await post({
    url: 'build/menuUrls/brands', 
    body: { 
      ...config.shop, 
      size,
      menu: process.env.NEXT_PUBLIC_MENU,
      token: process.env.BUILD_TOKEN,
    }, 
    local: true,
  })

  type item = { id: number; path: string }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj, 'id') && typeof obj.id === 'number' &&
      hasOwnProperty(obj, 'path') && typeof obj.path === 'string'
    )

  if (!isItems(data)) {
    throw new Error('Brand build paths could not be loaded.')
  }

  const paths = mergeListingRoutes(data, loadTemplateListingRoutes()?.brands).map(({ id, path }: item) => {
    const bid = id.toString()
    const b_alias = saveUrl(path || bid)
    return {
      params: { bid, b_alias },
    }
  })

  return {
    paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }
}

/* ------------------------------------------------------------------------------- */

interface IParams extends ParsedUrlQuery {
  bid: string
  b_alias: string
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { bid, b_alias } = params as IParams
  const bidNumber = Number(bid)

  const initialPage = listingPageNumber(params?.page)
  if (!initialPage) return { notFound: true, revalidate: 60 }

  const { data, siteSettings: readSiteSettings } = await loadListingPageData({
    ...config.shop,
    page: initialPage,
    size: 24,
    brandId: bidNumber,
    filterTree: true,
    filterBrands: true,
  })
  const siteSettings = await readSiteSettings()

  if (!data.brand || (initialPage > 1 && (initialPage - 1) * 24 >= data.filterProducts.count)) {
    return { notFound: true, revalidate: 60 }
  }

  const canonicalBrandAlias = saveUrl(data.brand?.path || b_alias || String(bidNumber))
  const canonicalUrl = listingPagePath(`b/${canonicalBrandAlias}/${bidNumber}`, initialPage)
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    data.landingPageImageAiGenerated,
    siteSettings.landingPageImageAiGenerated,
  )

  if ((params?.page !== undefined && initialPage === 1) || (canonicalBrandAlias && canonicalBrandAlias !== b_alias)) {
    return {
      redirect: {
        destination: `/${canonicalUrl}`,
        permanent: true,
      },
    }
  }

  return {
    props: {
      bid: bidNumber,
      ...withoutUndefined({
        title: data.brand?.titel,
        metaTitle: data.brand?.metaTitle,
        metaDescription: data.brand?.metaDescription,
        metaKeywords: data.brand?.metaKeywords,
        description: data.brand?.description,
      }),
      ...(data.brand?.logo ? { brandLogo: data.brand.logo } : {}),
      ...(data.brand?.pageBuilder ? { pageBuilder: data.brand.pageBuilder } : {}),
      ...(data.brand?.magazines ? { magazines: data.brand.magazines } : {}),
      count: data?.filterProducts.count || 0,
      products: data?.filterProducts.products || [],
      productsGeneratedAt: Date.now(),
      initialPage,
      filterMetadata: { filterTree: data.filterProducts.filterTree || [], filterBrands: data.filterProducts.filterBrands || [] },
      canonicalUrl,
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(landingPageImageAiGenerated ? { landingPageImageAiGenerated } : {}),
    },
    revalidate: 60,
  }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
